import React from 'react';
import {Link} from 'react-router';
import {walk} from 'core/common/utils/jsonUtils';
import securityService from 'core/security/securityService';
import isNumber = require("lodash/isNumber");
import isString = require("lodash/isString");
import {clone, addTenantToQuery} from "core/common/utils/utils";

import {FormGroup} from "react-bootstrap";
import {ControlLabel} from "react-bootstrap";
import {HelpBlock} from "react-bootstrap";
import {FormControl} from "react-bootstrap";
import {ObservableMap} from "mobx";
import MdEntity from "core/metamodel/mdEntity";
import {ID} from "core/metamodel/metaConstant";
import Form from "core/form/form";
import MdField from "core/metamodel/mdField";
import {MovingBlocks} from "core/grid/gridUtils";
import {FieldObjectType, FieldDefinitionType} from "core/form/form";
import uiService from "core/common/service/uiService";
import {DomainAncestor} from "core/common/domain/DomainAncestor";
import {ValidationMessage} from "core/common/domain/commonDomain";
import {LovItem} from "core/metamodel/mdEntity";
import {LoginObject} from "core/security/loginForm";



export interface EditedGridData {
  gridLocation?: string;
  deletedRowIds?: ID[];
  editedRows?: DomainAncestor[];
}

export type EditedGridDataObject = {
  [key: string]: EditedGridData;
}

export interface DataObjectDto<D extends IDomainObject> {
  dataObject: D;
  gridData?: EditedGridDataObject;
}



export interface IDomainObject {
  id?: ID;
  $dirty?: boolean;
  $new?: boolean;
  $added?: boolean;
  $errors?: ValidationMessage[];
  $formMap?:  ObservableMap<Form<DomainAncestor>>;
  $movingBlocks?: MovingBlocks;

  tenantId? : number;
  userCreated?: string;
  dateCreated?: string|Date;
  label?: string;
  active?: boolean;
  closeDate?: string|Date;
  "@class"?: string;
  tupleMap?: any;
}


export type TExtractFieldFn<D> = (dataObject: D, fieldDef: FieldDefinitionType) => MdField;


export interface FormProps<D extends DomainAncestor, R extends DomainAncestor> {
  dataObject: D;
  rootObject?: R;
  entities: ObservableMap<MdEntity>;
  entity?: MdEntity;
  disabled?: boolean;
  extractFieldFn?: TExtractFieldFn<D>;
  form?: Form<D>;

  validate?: Function;
  formName?: string;
  index?: number;

  onDelete?: Function;
  open?: boolean;
  onSubmit?: (dataObject: D, afterValidateFn?: () => void) => Promise<any>;
}


export interface ClosedContentProps<D extends DomainAncestor> {
  dataObject: D;
  rootObject?: any;
  entities: ObservableMap<MdEntity>;
  entity: MdEntity;
  disabled?: boolean;
  style?: any;
  fields: {[key in keyof D]: FieldObjectType};
}



export function isForTenant(...tenantIds) {
  let currentTenant = securityService.currentTenant;
  return (currentTenant && tenantIds.includes(currentTenant.tenantId));
}

export function showForTenant(Component, ...tenantIds) {
  return (isForTenant(...tenantIds)) ? Component : null;
}

export function hideForTenant(Component, ...tenantIds) {
  return (isForTenant(...tenantIds)) ? null : Component;
}

export function isForVatRateCountry(...countries) {
  let currentTenant = securityService.currentTenant;
  return (currentTenant && countries.includes(lovValue(currentTenant.vatRateCountry)));
}

export function showForVatRateCountry(Component, ...countries) {
  return (isForVatRateCountry(...countries)) ? Component : null;
}

export function hideForVatRateCountry(Component, ...countries) {
  return (isForVatRateCountry(...countries)) ? null : Component;
}



/**
 * zobrazi Component pokud value je true
 * @param Component
 * @param value
 * @returns {null}
 */
export function showForValue(Component, value) {
  return (value) ? Component : null;
}

export function getLovItem(lovItems, id) {
  return lovItems.find(lov => lov.value === id);
}

export function lovLabel(value): string {
  return ((value && typeof value.label !== 'undefined')?value.label:value);
}
export function lovValue(value) {
  return ((value && typeof value.value !== 'undefined')?value.value:value);
}
export function lovParam(value: LovItem|any, name: string) {
  return ((value) ? value[name] : null);
}

export function pickLov(object: any): LovItem {
  return (object && object.value) ? {value: object.value, label: object.label} : null;
}

export function replaceIdWithLov(value: string|number, entity: MdEntity): any {
  if (isNumber(value) || isString(value)) {
    return entity.getLovItem(value, false);
  } else {
    return value;
  }
}

export function formatValue(value, mdField: MdField) {
  return (mdField) ? mdField.formatValue(value) : lovLabel(value);
}


export interface FieldTextProps {
  label?: string;
  value?: any;
  mdField?: MdField;
  linkProps?: any;
  style?: any;
  alwaysShow?: boolean;
}

export const FieldText = ({label, value, mdField, linkProps, style, alwaysShow}: FieldTextProps) => ((value == null && !alwaysShow) ? <span/> : (
  (linkProps) ? (
    <Link {...linkProps} style={style} query={addTenantToQuery(linkProps.query)}>
      <span>
        {(label) ? <span className="zz-grey-text zz-mr-2">{label}: </span> : null}
        <span className="zz-mr-10">{formatValue(value, mdField)}</span>
      </span>
    </Link>
    ) : (
      <span style={style}>
      {(label) ? <span className="zz-grey-text zz-mr-2">{label}: </span> : null}
      <span className="zz-mr-10">{formatValue(value, mdField)}</span>
    </span>
  )
));



interface FormObjectHolder {
  form: Form<DomainAncestor>;
  object: DomainAncestor;
}

export function generateTuples(rootObject: any, tuples: FormObjectHolder[], validateAllForms: boolean) {
  walk(rootObject, (object , level) => {
    if (object.$formMap) {
      let _tuples: FormObjectHolder[] = object.$formMap.values().filter(form => form.$open || validateAllForms || form.nested).map(form => ({form: form, object: object}));
      tuples.push(..._tuples);
    }
  });
}



/**
 * 1. najde otevrene formy (ve $formMap), zkusi je zvalidovat, pokud OK, zavre je.
 * 2. pokud zadana customValidate funkce, tak ji zavola a chyby ulozi do rootObject.$errors
 * @param rootObject
 * @param customValidate
 * @param validateAllForms - ignoruje priznak form.$open
 * @returns {boolean} - true pokud ani 1. ani 2. nevrati zadnou chybu, jinak false
 */
export function preSave(rootObject: IDomainObject,  customValidate?: (rootObject: IDomainObject) => ValidationMessage[], validateAllForms?: boolean) {


  // najdu otevrene form objekty
  let openTuples: FormObjectHolder[] = [];
  generateTuples(rootObject, openTuples, validateAllForms);

  //// editable grids forms
  if (rootObject.$movingBlocks) {
    for(let grid of rootObject.$movingBlocks.getEditedBlocks()) {
      for(let rowObject of grid.getEditedRows()) {
        generateTuples(rowObject, openTuples, validateAllForms);
      }
    }
  }

  let cleanTuples = [];
  rootObject.$errors = [];

  console.log('Generated tuples %O on rootObject %O', openTuples, rootObject);
  for(let {form, object} of openTuples) {
    let res = form.validateForm(object, rootObject.$errors);
    if (res) {
      cleanTuples.push({form, object});
    }
  }

  if (customValidate) {
    rootObject.$errors = rootObject.$errors.concat(customValidate(rootObject));
  }

  let isOK =  (rootObject.$errors.length === 0);

  if (isOK) {
    for(let {form, object} of cleanTuples) {
      object.$added = undefined;
      form.$open = false;
      form.$pristineObject = undefined;
    }
  } else {
    uiService.hideToolbarActionFlag();
  }

  return isOK;
}





export function FieldGroup({ id = null, label = null, help = null, bsSize, ...props }) {
  return (
    <FormGroup controlId={id} bsSize={bsSize}>
      <ControlLabel>{label}</ControlLabel>
      <FormControl {...props} />
      {help && <HelpBlock>{help}</HelpBlock>}
    </FormGroup>
  );
}





// WEBPACK FOOTER //
// ./src/core/form/formUtils.tsx